// @ts-strict-ignore
import React, { useEffect, useMemo } from 'react'
import { UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, type RadioOption } from '@node-space/ui-components'
import { PAYMENT_METHODS_LABELS } from 'pages/Contacts/constants'
import {
  BeneficiaryDetailsCurrency,
  PaymentMethodLabels,
  PaymentMethods,
} from 'types/beneficiaries'
import { ContactPaymentInstruments } from 'types/contacts'
import { SEPA_CREDIT_INSTANT_TRANSFER_LIMIT } from '../../constants'
import { FiatSendFormData } from '../../form/fiatSendFormSchema'
import { getAvailablePaymentMethods } from '../../utils'
import { AchSameDayCheckbox } from './AchSameDayCheckbox'
import { PaymentMethodGroup } from './PaymentMethodGroup'

const PaymentMethodDescriptionKeys = {
  [PaymentMethods.SEPA]: 'wallets.fiatSend.paymentMethodDescriptions.SEPA_CT',
  [PaymentMethods.SEPA_INSTANT]: 'wallets.fiatSend.paymentMethodDescriptions.SEPA_INST',
  [PaymentMethods.FASTER_PAYMENT]: 'wallets.fiatSend.paymentMethodDescriptions.FASTER_PAYMENT',
  [PaymentMethods.ACH]: 'wallets.fiatSend.paymentMethodDescriptions.ACH',
  [PaymentMethods.ACH_SAME_DAY]: 'wallets.fiatSend.paymentMethodDescriptions.ACH_SAME_DAY',
  [PaymentMethods.FEDWIRE]: 'wallets.fiatSend.paymentMethodDescriptions.FEDWIRE',
  [PaymentMethods.FEDNOW]: 'wallets.fiatSend.paymentMethodDescriptions.FEDNOW',
  [PaymentMethods.CRNOW]: 'wallets.fiatSend.paymentMethodDescriptions.CRNOW',
  [PaymentMethods.RTP]: 'wallets.fiatSend.paymentMethodDescriptions.RTP',
  [PaymentMethods.SWIFT]: 'wallets.fiatSend.paymentMethodDescriptions.SWIFT',
}

interface PaymentMethodSelectionProps {
  formValues: FiatSendFormData
  contactPaymentInstrument: ContactPaymentInstruments
  currency: string
  amount?: string
  error?: string
  register: UseFormRegister<FiatSendFormData>
  setValue: UseFormSetValue<FiatSendFormData>
}

export const PaymentMethodSelection = ({
  formValues,
  contactPaymentInstrument,
  currency,
  amount,
  error,
  register,
  setValue,
}: PaymentMethodSelectionProps) => {
  const { t } = useTranslation()

  const availablePaymentMethods = useMemo(
    () => getAvailablePaymentMethods(contactPaymentInstrument),
    [contactPaymentInstrument]
  )

  const paymentMethodOptions: RadioOption[] = useMemo(() => {
    const paymentMethodOptions: RadioOption[] = []

    switch (currency) {
      case BeneficiaryDetailsCurrency.EUR: {
        const sepa = availablePaymentMethods.find(
          methodSupport => methodSupport?.paymentMethod === PaymentMethods.SEPA
        )
        const sepaInstant = availablePaymentMethods.find(
          methodSupport => methodSupport?.paymentMethod === PaymentMethods.SEPA_INSTANT
        )
        const swift = availablePaymentMethods.find(
          methodSupport => methodSupport?.paymentMethod === PaymentMethods.SWIFT
        )

        if (sepa || sepaInstant) {
          paymentMethodOptions.push(
            {
              label: `${t('wallets.fiatSend.autoPaymentMethod')} (${t('wallets.fiatSend.recommended')})`,
              value: '',
              secondaryLabel: t('wallets.fiatSend.autoPaymentMethodDescription'),
              disabled: !sepa?.supported && !sepaInstant?.supported,
            },
            {
              label: t(PAYMENT_METHODS_LABELS.SEPA_INST),
              value: PaymentMethods.SEPA_INSTANT,
              secondaryLabel: sepaInstant?.supported
                ? t(PaymentMethodDescriptionKeys[PaymentMethods.SEPA_INSTANT] || '')
                : t('wallets.fiatSend.eligibilityMessageMapping.paymentMethodUnavailable', {
                    paymentMethods: t(PAYMENT_METHODS_LABELS[PaymentMethods.SEPA_INSTANT]),
                  }),
              disabled:
                Number(amount) > SEPA_CREDIT_INSTANT_TRANSFER_LIMIT || !sepaInstant?.supported,
            },
            {
              label: t(PAYMENT_METHODS_LABELS.SEPA_CT),
              value: PaymentMethods.SEPA,
              secondaryLabel: sepa?.supported
                ? t(PaymentMethodDescriptionKeys[PaymentMethods.SEPA] || '')
                : t('wallets.fiatSend.eligibilityMessageMapping.paymentMethodUnavailable', {
                    paymentMethods: t(PAYMENT_METHODS_LABELS[PaymentMethods.SEPA]),
                  }),
              disabled: !sepa?.supported,
            }
          )
        }

        if (swift) {
          paymentMethodOptions.push({
            label: t(PAYMENT_METHODS_LABELS.SWIFT),
            value: PaymentMethods.SWIFT,
            secondaryLabel: swift?.supported
              ? t(PaymentMethodDescriptionKeys[PaymentMethods.SWIFT] || '')
              : t('wallets.fiatSend.eligibilityMessageMapping.paymentMethodUnavailable', {
                  paymentMethods: t(PAYMENT_METHODS_LABELS[PaymentMethods.SWIFT]),
                }),
            disabled: !swift?.supported,
          })
        }
        break
      }
      case BeneficiaryDetailsCurrency.USD: {
        const usdOptions = availablePaymentMethods.map(methodSupport => ({
          label: t(
            PAYMENT_METHODS_LABELS[methodSupport?.paymentMethod] ?? methodSupport?.paymentMethod
          ),
          secondaryLabel: methodSupport?.supported
            ? t(PaymentMethodDescriptionKeys[methodSupport?.paymentMethod] ?? '')
            : t('wallets.fiatSend.eligibilityMessageMapping.paymentMethodUnavailable', {
                paymentMethods: t(
                  PAYMENT_METHODS_LABELS[methodSupport?.paymentMethod] ??
                    methodSupport?.paymentMethod
                ),
              }),
          value: methodSupport?.paymentMethod,
          disabled: !methodSupport?.supported,
        }))
        paymentMethodOptions.push(...usdOptions)
        break
      }
      case BeneficiaryDetailsCurrency.GBP: {
        const gbpOptions = availablePaymentMethods.map(methodSupport => ({
          label: t(
            PAYMENT_METHODS_LABELS[methodSupport?.paymentMethod] ?? methodSupport?.paymentMethod
          ),
          secondaryLabel: methodSupport?.supported
            ? t(PaymentMethodDescriptionKeys[methodSupport?.paymentMethod] ?? '')
            : t('wallets.fiatSend.eligibilityMessageMapping.paymentMethodUnavailable', {
                paymentMethods: t(
                  PAYMENT_METHODS_LABELS[methodSupport?.paymentMethod] ??
                    methodSupport?.paymentMethod
                ),
              }),
          value: methodSupport?.paymentMethod,
          disabled: !methodSupport?.supported,
        }))
        paymentMethodOptions.push(...gbpOptions)
        break
      }
      default:
    }

    return paymentMethodOptions
  }, [availablePaymentMethods, amount, currency, t])

  useEffect(() => {
    const isSingleOption = paymentMethodOptions?.length === 1
    const isSwiftAvailable = paymentMethodOptions?.some(
      option => option.value === PaymentMethods.SWIFT
    )
    const isSwiftOnly = isSwiftAvailable && isSingleOption
    const isPaymentMethodSet = !!formValues?.paymentMethod

    if (isSwiftOnly) {
      setValue('paymentMethod', PaymentMethods.SWIFT)
      return
    }

    // Set if only one option
    if (isSingleOption) {
      setValue('paymentMethod', paymentMethodOptions?.[0]?.value ?? '')
      return
    }

    if (!isSingleOption && !isPaymentMethodSet) {
      const firstEnabledOption = paymentMethodOptions?.find(option => !option.disabled)
      setValue('paymentMethod', firstEnabledOption?.value ?? '')
    }
  }, [paymentMethodOptions, currency, formValues?.paymentMethod, setValue])

  if (!paymentMethodOptions?.length) return null

  return (
    <>
      {paymentMethodOptions?.length === 1 ? (
        <DataListItem
          layout="vertical"
          id="payment-method-item"
          title={t('paymentMethod')}
          body={PaymentMethodLabels[formValues?.paymentMethod] || formValues?.paymentMethod}
        />
      ) : (
        <PaymentMethodGroup
          formValues={formValues}
          paymentMethodOptions={paymentMethodOptions}
          error={error}
          setValue={setValue}
        />
      )}

      {formValues?.paymentMethod === PaymentMethods.ACH && (
        <Box paddingT={12} paddingB={8}>
          <AchSameDayCheckbox formValues={formValues} register={register} setValue={setValue} />
        </Box>
      )}
    </>
  )
}
